import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import client from '../../api/client';
import Navbar from '../../components/Navbar';

const STATUS_DOT = {
  pending:  '#94a3b8',
  assigned: '#3b82f6',
  labeled:  '#f59e0b',
  approved: '#22c55e',
  rejected: '#ef4444',
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    client.get(`/api/projects/${id}/`)
      .then(res => setProject(res.data))
      .catch(() => setError('Failed to load project.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await client.get(`/api/projects/${id}/export/`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div style={styles.page}><Navbar /><p style={styles.hint}>Loading…</p></div>;
  if (error)   return <div style={styles.page}><Navbar /><p style={styles.errorText}>{error}</p></div>;

  const counts = project.status_counts || {};
  const total = project.images.length;
  const approved = counts.approved || 0;
  const pct = total > 0 ? Math.round((approved / total) * 100) : 0;

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>← Back</button>
            <h1 style={styles.title}>{project.name}</h1>
            <span style={{ ...styles.badge, ...statusColor(project.status) }}>{project.status}</span>
          </div>
          <button
            style={{ ...styles.exportBtn, opacity: exporting ? 0.7 : 1 }}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export COCO JSON'}
          </button>
        </div>

        {/* Progress bar */}
        <div style={styles.card}>
          <div style={styles.progressLabel}>
            <span>Approved</span>
            <span>{approved} / {total} images ({pct}%)</span>
          </div>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${pct}%` }} />
          </div>
        </div>

        {/* Status counts */}
        <div style={styles.countsRow}>
          {Object.entries(counts).map(([s, n]) => (
            <div key={s} style={styles.countCard}>
              <span style={{ ...styles.dot, background: STATUS_DOT[s] || '#94a3b8' }} />
              <span style={styles.countNum}>{n}</span>
              <span style={styles.countLabel}>{s}</span>
            </div>
          ))}
        </div>

        {/* Image grid */}
        {project.images.length > 0 && (
          <div style={styles.grid}>
            {project.images.map(img => (
              <div key={img.id} style={styles.imgCard}>
                <div style={styles.imgWrap}>
                  <img
                    src={img.signed_url}
                    alt={img.filename}
                    style={styles.thumb}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <span style={{ ...styles.statusDot, background: STATUS_DOT[img.status] || '#94a3b8' }} />
                </div>
                <p style={styles.imgName}>{img.filename}</p>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function statusColor(s) {
  if (s === 'active')   return { background: '#d4edda', color: '#155724' };
  if (s === 'complete') return { background: '#cce5ff', color: '#004085' };
  return { background: '#fff3cd', color: '#856404' };
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'sans-serif' },
  content: { padding: '2rem', maxWidth: '960px', margin: '0 auto' },
  hint: { padding: '2rem', color: '#888' },
  errorText: { padding: '2rem', color: '#c0392b' },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  backBtn: {
    background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px',
    padding: '0.4rem 0.8rem', cursor: 'pointer', fontSize: '0.85rem', color: '#555',
  },
  title: { margin: 0, fontSize: '1.5rem', color: '#2c3e50' },
  badge: {
    display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '999px',
    fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize',
  },
  exportBtn: {
    background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '6px',
    padding: '0.6rem 1.2rem', fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap',
  },

  card: {
    background: '#fff', borderRadius: '8px', padding: '1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)', marginBottom: '1rem',
  },
  progressLabel: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '0.875rem', color: '#555', marginBottom: '0.5rem',
  },
  progressTrack: {
    height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: '#22c55e', borderRadius: '999px',
    transition: 'width 0.3s ease',
  },

  countsRow: {
    display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem',
  },
  countCard: {
    background: '#fff', borderRadius: '8px', padding: '0.75rem 1rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex',
    alignItems: 'center', gap: '0.5rem', minWidth: '100px',
  },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  countNum: { fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50' },
  countLabel: { fontSize: '0.78rem', color: '#666', textTransform: 'capitalize' },

  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem',
  },
  imgCard: {
    background: '#fff', borderRadius: '8px', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  },
  imgWrap: { position: 'relative', aspectRatio: '4/3', background: '#f1f5f9', overflow: 'hidden' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  statusDot: {
    position: 'absolute', top: '8px', right: '8px',
    width: '12px', height: '12px', borderRadius: '50%',
    border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  imgName: {
    margin: 0, padding: '0.5rem 0.75rem', fontSize: '0.78rem',
    color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
};
